import { isEmpty } from '#misc/is-empty';
import { isNotDefined } from '#misc/is-not-defined.guard';

/**
 * Converts an array of objects into a flat query string parameter object.
 *
 * Each property of each object in the array is mapped to a key in the format:
 * `${baseKey}[index].propertyName`, where `index` is the position of the object in the array.
 *
 * If the input array is `undefined`, empty, or contains only undefined items, an empty object is returned.
 *
 * @template TObject - The type of objects contained in the array.
 * @param items - The array of objects to convert, or `undefined`.
 * @param baseKey - The base key to use for each query string parameter.
 * @returns An object mapping flattened keys to their corresponding values.
 */
export const convertToQueryString = <TObject extends Record<PropertyKey, unknown>>(
  items: TObject[] | undefined,
  baseKey: string
) => {
  if (isNotDefined(items) || isEmpty(items)) {
    return {};
  }

  const params: Record<string, unknown> = {};

  for (let index = 0; index < items.length; index++) {
    const item = items[index];

    if (isNotDefined(item)) {
      continue;
    }

    for (const [key, value] of Object.entries(item)) {
      if (Object.hasOwn(item, key)) {
        const flatKey = `${baseKey}[${index}].${key}`;

        params[flatKey] = value;
      }
    }
  }

  return params;
};
